import { createSlice } from "@reduxjs/toolkit";
import {
    getAllPropertiesByCaseIdAction,
    getPropertyByIdAction,
    createPropertyAction,
    updatePropertyAction,
    deletePropertyAction,
    searchPropertiesAction,
    bulkCreatePropertiesAction,
    getPropertiesWithPaginationAction,
    exportPropertiesToExcelAction,
    clearPropertyStateAction,
    setSelectedPropertyAction,
    setPropertyFiltersAction
} from "../../actions/Case/casePropertyDetailsAssetAction";

const initialState = {
    properties: [],
    assetOwned: {},
    selectedProperty: null,
    filters: {},
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
    },
    loading: false,
    error: null,
    success: false,
    message: "",
    exportLoading: false,
    exportError: null,
    exportData: null
};

const propertySlice = createSlice({
    name: "property",
    initialState,
    reducers: {
        resetPropertyState: (state) => {
            return initialState;
        },
        clearPropertyError: (state) => {
            state.error = null;
            state.message = "";
        },
        updatePropertyInList: (state, action) => {
            const index = state.properties.findIndex(
                p => p.propertyID === action.payload.propertyID
            );
            if (index !== -1) {
                state.properties[index] = { ...state.properties[index], ...action.payload };
            }
        },
        removePropertyFromList: (state, action) => {
            state.properties = state.properties.filter(
                p => p.propertyID !== action.payload
            );
        },
        clearSelectedProperty: (state) => {
            state.selectedProperty = null;
        }
    },
    extraReducers: (builder) => {
        // =========== GET ALL PROPERTIES BY CASE ID ===========
        builder
            .addCase(getAllPropertiesByCaseIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getAllPropertiesByCaseIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.properties = action.payload.properties;
                state.assetOwned = action.payload.assetOwnedBy;
                state.message = action.payload.message;
            })
            .addCase(getAllPropertiesByCaseIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch properties";
                state.properties = [];
            })

        // =========== GET SINGLE PROPERTY BY ID ===========
        builder
            .addCase(getPropertyByIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getPropertyByIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.selectedProperty = action.payload.property;
                state.message = action.payload.message;
            })
            .addCase(getPropertyByIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch property";
                state.selectedProperty = null;
            })

        // =========== CREATE NEW PROPERTY ===========
        builder
            .addCase(createPropertyAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createPropertyAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.properties.unshift(action.payload.property);
                state.message = action.payload.message;
            })
            .addCase(createPropertyAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to create property";
            })

        // =========== UPDATE PROPERTY ===========
        builder
            .addCase(updatePropertyAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updatePropertyAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.properties.findIndex(
                    p => p.propertyID === action.payload.propertyID
                );
                if (index !== -1) {
                    state.properties[index] = action.payload.property;
                }

                if (state.selectedProperty?.propertyID === action.payload.propertyID) {
                    state.selectedProperty = action.payload.property;
                }
                state.message = action.payload.message;
            })
            .addCase(updatePropertyAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update property";
            })

        // =========== DELETE PROPERTY ===========
        builder
            .addCase(deletePropertyAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deletePropertyAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                state.properties = state.properties.filter(
                    p => p.propertyID !== action.payload.propertyID
                );

                if (state.selectedProperty?.propertyID === action.payload.propertyID) {
                    state.selectedProperty = null;
                }
                state.message = action.payload.message;
            })
            .addCase(deletePropertyAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to delete property";
            })

        // =========== SEARCH PROPERTIES ===========
        builder
            .addCase(searchPropertiesAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(searchPropertiesAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.properties = action.payload.properties;
                state.filters = action.payload.searchCriteria;
                state.message = action.payload.message;
            })
            .addCase(searchPropertiesAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to search properties";
                state.properties = [];
            })

        // =========== BULK CREATE PROPERTIES ===========
        builder
            .addCase(bulkCreatePropertiesAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(bulkCreatePropertiesAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                state.properties = [...action.payload.properties, ...state.properties];
                state.message = action.payload.message;
            })
            .addCase(bulkCreatePropertiesAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to bulk create properties";
            })

        // =========== GET PROPERTIES WITH PAGINATION ===========
        builder
            .addCase(getPropertiesWithPaginationAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getPropertiesWithPaginationAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.properties = action.payload.properties;
                state.pagination = action.payload.pagination;
                state.message = action.payload.message;
            })
            .addCase(getPropertiesWithPaginationAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch properties";
            })

        // =========== EXPORT PROPERTIES TO EXCEL ===========
        builder
            .addCase(exportPropertiesToExcelAction.pending, (state) => {
                state.exportLoading = true;
                state.exportError = null;
            })
            .addCase(exportPropertiesToExcelAction.fulfilled, (state, action) => {
                state.exportLoading = false;
                state.exportData = action.payload.blobData;
                state.message = action.payload.message;
            })
            .addCase(exportPropertiesToExcelAction.rejected, (state, action) => {
                state.exportLoading = false;
                state.exportError = action.payload?.message || "Failed to export properties";
            })

        // =========== MATCHERS ===========
        builder
            .addMatcher(
                (action) => action.type === clearPropertyStateAction.type,
                () => initialState
            )
            .addMatcher(
                (action) => action.type === setSelectedPropertyAction.type,
                (state, action) => {
                    state.selectedProperty = action.payload;
                }
            )
            .addMatcher(
                (action) => action.type === setPropertyFiltersAction.type,
                (state, action) => {
                    state.filters = action.payload;
                }
            );
    }
});

export const {
    resetPropertyState,
    clearPropertyError,
    updatePropertyInList,
    removePropertyFromList,
    clearSelectedProperty
} = propertySlice.actions;

export default propertySlice.reducer;
